import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import PixelSnow from './components/PixelSnow';
import PdfViewer from './components/PdfViewer/PdfViewer';
import FloatingCart from './components/FloatingCart';
import Navbar from './components/Navbar';
import './CourseDetailsPage.css';
import './NotesDetailsPage.css';

export default function NoteDetailsPage() {
  const { id } = useParams();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pdfTotalPages, setPdfTotalPages] = useState(null);
  const [purchasedPages, setPurchasedPages] = useState(null);
  const navigate = useNavigate();

  const handleAddToCart = () => {
    if (!note) return;
    const cart = JSON.parse(localStorage.getItem('engimitra_cart') || '[]');
    const newItem = {
      id: note.id,
      item_type: 'note',
      title: note.title,
      thumbnail_url: note.thumbnail_url || 'https://images.unsplash.com/photo-1517842645767-c639042777db?q=80&w=600&auto=format&fit=crop',
      price: typeof note.price === 'number' ? note.price : 0,
      original_price: note.original_price || (typeof note.price === 'number' ? note.price * 2 : 0)
    };
    if (!cart.some(item => item.id === note.id)) {
      cart.push(newItem);
      localStorage.setItem('engimitra_cart', JSON.stringify(cart));
    }
    navigate('/cart');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchNote();
    checkPurchase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      if (!isFull) {
        // Reset zoom if it exceeds standard max when exiting fullscreen
        setZoomLevel(z => Math.min(z, 1.25));
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const fetchNote = async () => {
    try {
      setLoading(true);
      // pdf_url/all_pages (the paid content) are intentionally excluded here —
      // they're fetched separately, server-side, only after purchase is
      // verified. Selecting '*' would leak the paid file path to anyone who
      // opens the network tab, regardless of whether they've paid.
      const { data, error } = await supabase.from('notes')
        .select('id, title, description, category, type, university, pages, price, original_price, thumbnail_url, preview_pdf_url, includes, tags')
        .eq('id', id).single();
      if (error) throw error;
      setNote(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkPurchase = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('purchases').select('id')
      .eq('user_id', user.id).eq('item_id', id).eq('item_type', 'note').single();
    if (data) setHasPurchased(true);
  };

  // Fetch the paid page-image array (if this note ships as images rather than
  // a PDF) only once purchase is confirmed, via the entitlement-checked API —
  // never selected directly from the notes table.
  useEffect(() => {
    if (!hasPurchased) return;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      try {
        const res = await fetch(`/api/get-signed-url?itemType=note&itemId=${encodeURIComponent(id)}&variant=full`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        const data = await res.json();
        if (res.ok && data.pages) setPurchasedPages(data.pages);
      } catch (err) {
        console.error('Error fetching purchased pages:', err);
      }
    })();
  }, [hasPurchased, id]);

  // --- Skeleton ---
  if (loading) {
    return (
      <div className="cdp-container">
        <Navbar />
        <div className="cdp-skeleton-container">
          <div className="cdp-skel-left">
            <div className="cdp-skeleton cdp-skel-bc" />
            <div className="cdp-skeleton" style={{ width: '100%', aspectRatio: '1 / 1.414', borderRadius: '16px', marginBottom: '1.5rem' }} />
            <div className="cdp-skeleton cdp-skel-title" />
            <div className="cdp-skeleton cdp-skel-section" />
          </div>
          <div className="cdp-skel-right"><div className="cdp-skeleton cdp-skel-sidebar" /></div>
        </div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="cdp-container">
        <Navbar />
        <div className="cdp-error">
          <h1>Note Not Found</h1>
          <p>{error || 'This note does not exist or has been removed.'}</p>
          <Link to="/notes" className="cp-btn" style={{ textDecoration: 'none' }}>Back to Notes</Link>
        </div>
      </div>
    );
  }

  // preview_pages: array of image URLs for free preview
  // The full content (pdf_url / all_pages) is never fetched into this
  // component's state — it's resolved server-side, post-purchase-check, by
  // PdfViewer (secure mode) / the purchasedPages effect above. Whether this
  // note is PDF-based or image-based is inferred from the preview, since a
  // note's content type doesn't change between preview and full versions.
  const previewPages = note.preview_pages || [];
  const isPdfType = !!note.preview_pdf_url;
  const allPages = hasPurchased ? (purchasedPages || []) : [];
  const visiblePages = hasPurchased ? allPages : previewPages;
  const dbTotalPages = note.pages || allPages.length || previewPages.length || 0;

  let maxPages = 1;
  if (hasPurchased) {
    maxPages = isPdfType ? (pdfTotalPages || dbTotalPages || 1) : (allPages.length || 1);
  } else {
    const previewCount = isPdfType ? (pdfTotalPages || 1) : previewPages.length;
    maxPages = previewCount > 0 ? previewCount + 1 : 0;
  }
  const totalPagesDisplay = maxPages;
  const discount = note.original_price && note.price ? Math.round((1 - note.price / note.original_price) * 100) : null;

  const goToPage = (idx) => {
    if (idx >= 0 && idx < maxPages) {
      setCurrentPage(idx);
      document.querySelector('.pv-viewer-area')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleFullscreen = () => {
    const viewer = document.querySelector('.pv-workspace-container');
    if (!document.fullscreenElement) {
      viewer?.requestFullscreen?.().catch(err => console.log(err));
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <div className="cdp-container">
      <div className="cdp-pixel-bg">
        <PixelSnow color="#00e5c8" flakeSize={0.013} minFlakeSize={1.25} pixelResolution={230}
          speed={1.25} density={0.25} direction={125} brightness={0.7} variant="snowflake" />
      </div>

      <Navbar />

      <main className="cdp-main">
        {/* Breadcrumb */}
        <div className="cdp-breadcrumb">
          <Link to="/">Home</Link><span className="cdp-bc-sep">›</span>
          <Link to="/notes">Notes</Link><span className="cdp-bc-sep">›</span>
          <span className="cdp-bc-current">{note.title}</span>
        </div>

        {/* Now Viewing Badge */}
        <div className="pv-now-viewing">
          <div className="pv-dot"></div>
          <span>PREVIEWING: {note.title}</span>
          {hasPurchased && <span className="pv-unlocked-badge"><span className="material-symbols-outlined">lock_open</span> FULL ACCESS</span>}
        </div>

        <div className="cdp-grid">
          {/* ===== LEFT: PDF VIEWER ===== */}
          <div style={{ minWidth: 0, width: '100%' }}>
            <div className="pv-workspace-container" style={{ display: 'flex', flexDirection: 'column', background: 'var(--bg)', width: '100%', overflow: 'hidden' }}>
              {/* Viewer Toolbar */}
            <div className="pv-toolbar">
              <div className="pv-toolbar-left">
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 0} className="pv-tool-btn">
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <span className="pv-page-indicator">
                  Page <strong>{currentPage + 1}</strong> of <strong>{totalPagesDisplay} {hasPurchased ? '' : '(preview)'}</strong>
                </span>
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= maxPages - 1} className="pv-tool-btn">
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
                </div>
                <div className="pv-toolbar-right">
                  <button onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.25))} className="pv-tool-btn" title="Zoom Out">
                    <span className="material-symbols-outlined">zoom_out</span>
                  </button>
                  <span className="pv-zoom-label">{Math.round(zoomLevel * 100)}%</span>
                  <button onClick={() => setZoomLevel(z => Math.min(isFullscreen ? 2 : 1.25, z + 0.25))} disabled={zoomLevel >= (isFullscreen ? 2 : 1.25)} className="pv-tool-btn" title="Zoom In">
                    <span className="material-symbols-outlined">zoom_in</span>
                  </button>
                  <button onClick={() => setZoomLevel(1)} className="pv-tool-btn" title="Reset Zoom">
                    <span className="material-symbols-outlined">fit_screen</span>
                  </button>
                  <button onClick={toggleFullscreen} className="pv-tool-btn" title="Fullscreen" style={{ marginLeft: '0.5rem', color: 'var(--cyan)' }}>
                    <span className="material-symbols-outlined">fullscreen</span>
                  </button>
                </div>
              </div>

            {/* Page Viewer */}
            <div className="pv-viewer-area" onContextMenu={e => e.preventDefault()}>
              <div style={{ display: (!hasPurchased && currentPage === maxPages - 1) ? 'none' : 'contents' }}>
                {isPdfType ? (
                  <PdfViewer
                    {...(hasPurchased
                      ? { secure: true, itemType: 'note', itemId: note.id }
                      : { filePath: note.preview_pdf_url })}
                    pageNumber={currentPage + 1}
                    zoomLevel={zoomLevel}
                    onLoadSuccess={({ numPages }) => setPdfTotalPages(numPages)}
                  />
                ) : visiblePages.length > 0 ? (
                  <div className="pv-page-container" style={{ transform: `scale(${zoomLevel})` }}>
                    <img
                      src={visiblePages[currentPage]}
                      alt={`Page ${currentPage + 1}`}
                      className="pv-page-img"
                      draggable={false}
                      style={{ userSelect: 'none' }}
                    />
                  </div>
                ) : (
                  <div className="pv-empty-state">
                    <span className="material-symbols-outlined">menu_book</span>
                    <h3>Preview pages coming soon</h3>
                    <p>The author is uploading preview pages for this document.</p>
                  </div>
                )}
              </div>

              {/* Paywall after last preview page */}
              {!hasPurchased && currentPage === maxPages - 1 && (
                <div className="pv-paywall" style={{ height: '100%', alignItems: 'center', borderTop: 'none', background: 'transparent' }}>
                  <div className="pv-paywall-inner">
                    <span className="material-symbols-outlined pv-paywall-icon">lock</span>
                    <h3>You've reached the end of the free preview</h3>
                    <p>Purchase to unlock all {dbTotalPages} pages and read the full document.</p>
                    <button className="pv-paywall-btn" onClick={handleAddToCart}>
                      <span className="material-symbols-outlined">shopping_cart</span>
                      Unlock Full Access — {typeof note.price === 'number' ? `₹${note.price}` : 'Buy Now'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Page Thumbnails Strip */}
            {(!isPdfType && visiblePages.length > 1) && (
              <div className="pv-thumbs-strip">
                {visiblePages.map((page, i) => (
                  <button
                    key={i}
                    className={`pv-thumb ${currentPage === i ? 'active' : ''}`}
                    onClick={() => goToPage(i)}
                  >
                    <img src={page} alt={`Thumb ${i + 1}`} draggable={false} />
                    <span className="pv-thumb-num">{i + 1}</span>
                  </button>
                ))}
                {!hasPurchased && dbTotalPages > previewPages.length && (
                  <div className="pv-thumb pv-thumb-locked">
                    <span className="material-symbols-outlined">lock</span>
                    <span className="pv-thumb-num">+{dbTotalPages - previewPages.length}</span>
                  </div>
                )}
              </div>
            )}
            </div>

            {/* About Section */}
            {note.description && (
              <section className="cdp-section" style={{ marginTop: '2rem' }}>
                <h2 className="cdp-section-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                  About This PDF
                </h2>
                <p className="cdp-about-text">{note.description}</p>
              </section>
            )}

            {/* What's Included */}
            {note.includes && note.includes.length > 0 && (
              <section className="cdp-section">
                <h2 className="cdp-section-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                  What's Included
                </h2>
                <div className="pv-includes-grid">
                  {note.includes.map((item, i) => (
                    <div key={i} className="pv-include-chip">
                      <span className="material-symbols-outlined">check_circle</span>
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Tags */}
            {note.tags && note.tags.length > 0 && (
              <div className="ndp-tags-section">
                <h3 className="ndp-tags-title">Related Topics</h3>
                <div className="ndp-tags-wrap">
                  {note.tags.map((tag, i) => <span key={i} className="ndp-tag">{tag}</span>)}
                </div>
              </div>
            )}
          </div>

          {/* ===== RIGHT: PURCHASE SIDEBAR ===== */}
          <aside className="cdp-sidebar-sticky">
            <div className="ndp-purchase-card">
              <div className="ndp-purchase-header">
                {note.thumbnail_url && <img src={note.thumbnail_url} alt="" className="pv-sidebar-thumb" />}
                <div className="ndp-purchase-price-row" style={{ padding: note.thumbnail_url ? '1rem 1.5rem 0' : '1.5rem 1.5rem 0' }}>
                  <span className="ndp-price-big">{typeof note.price === 'number' ? `₹${note.price}` : (note.price || 'Free')}</span>
                  {note.original_price && <span className="ndp-price-strike">₹{note.original_price}</span>}
                  {discount && <span className="ndp-discount-badge">{discount}% OFF</span>}
                </div>
              </div>

              <div className="ndp-purchase-body">
                {hasPurchased ? (
                  <div className="ndp-owned-state">
                    <div className="ndp-owned-badge">
                      <span className="material-symbols-outlined">check_circle</span> Full Access Unlocked
                    </div>
                    <button className="ndp-cta-btn" onClick={() => document.querySelector('.pv-viewer-area')?.scrollIntoView({ behavior: 'smooth' })}>
                      <span className="material-symbols-outlined">menu_book</span> Read Document Now
                    </button>
                  </div>
                ) : (
                  <>
                    <button className="ndp-cta-btn" onClick={handleAddToCart}>
                      <span className="material-symbols-outlined">lock_open</span> Unlock Full PDF
                    </button>
                    <p className="ndp-cta-sub">Instant access • All {dbTotalPages} pages</p>
                  </>
                )}

                <div className="ndp-sidebar-includes">
                  <h4>You'll get:</h4>
                  <ul>
                    <li><span className="material-symbols-outlined">picture_as_pdf</span> {dbTotalPages || '?'}-page full document</li>
                    <li><span className="material-symbols-outlined">event_available</span> 6 months platform access</li>
                    <li><span className="material-symbols-outlined">smartphone</span> Mobile & desktop ready</li>
                    <li><span className="material-symbols-outlined">shield</span> Secure premium viewer</li>
                    <li><span className="material-symbols-outlined">verified</span> Expert verified content</li>
                  </ul>
                </div>

                {/* Meta Info */}
                <div className="pv-sidebar-meta">
                  {note.category && <div className="pv-meta-row"><span className="pv-meta-label">Subject</span><span className="pv-meta-value">{note.category}</span></div>}
                  {note.type && <div className="pv-meta-row"><span className="pv-meta-label">Type</span><span className="pv-meta-value">{note.type}</span></div>}
                  {note.university && <div className="pv-meta-row"><span className="pv-meta-label">University</span><span className="pv-meta-value">{note.university}</span></div>}
                  {note.pages && <div className="pv-meta-row"><span className="pv-meta-label">Pages</span><span className="pv-meta-value">{note.pages}</span></div>}
                </div>

                <div className="ndp-guarantee">
                  <span className="material-symbols-outlined">shield</span>
                  <span>Secure payment via Razorpay</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <footer className="em-footer">
        <div className="fg">
          <div className="fb">
            <a href="/" className="em-logo"><span className="l1">ENGI</span><span className="l2">&lt;mitra&gt;</span></a>
            <p>Built for 1st year engineering students. Expert notes, courses, and MPQs for every major Indian university — since 2022.</p>
          </div>
          <div className="fc">
            <h5>Platform</h5>
            <ul>{["Courses", "Notes", "MPQs"].map(l => <li key={l}><a href={l === 'Courses' ? '/courses' : `/${l.toLowerCase()}`}>{l}</a></li>)}</ul>
          </div>
          <div className="fc">
            <h5>Community</h5>
            <ul>
              <li><a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer">Instagram</a></li>
              <li><a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer">Facebook</a></li>
              <li><a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer">WhatsApp Group</a></li>
            </ul>
          </div>
          <div className="fc">
            <h5>Legal</h5>
            <ul>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>
        </div>
        <div className="fb-bottom">
          <p>© 2026 ENGImitra. All rights reserved.</p>
          <div className="soc-row">
            <a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer" className="soc" aria-label="Instagram">ig</a>
            <a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer" className="soc" aria-label="Facebook">fb</a>
            <a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer" className="soc" aria-label="WhatsApp Group">wa</a>
          </div>
        </div>
      </footer>
      <FloatingCart />
    </div>
  );
}
